import {
    Space, Spin, Typography, Upload,
} from 'antd';
import Button from 'antd/es/button';
import {RcFile} from 'antd/es/upload';
import {isNil} from 'lodash-es';
import React, {useEffect, useState, FC} from 'react';

import {ConfirmModal} from 'components/confirm-modal/confirm-modal';
import {SimpleActionButton} from 'components/form/components';
import {REQUESTS_ENTITY_NAME} from 'components/request/constants/requests.constants';
import {SignWithCryptoProInput} from 'features/sign-with-crypto-pro';
import {createActionGetFile} from 'modules/data/data-actions';
import {useUserRoleFunctions} from 'shared/hooks/use-user-role-functions';
import {IFileDto, UploadFile} from 'shared/types/files';
import {ERequestType} from 'shared/types/requests';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    EDocumentsSigningMethod,
    loadDocumentsSigningMethod,
    selectDocumentsSigningMethod,
} from 'store/slices/documents-uploading-slice';
import {EUserRoleFunction} from 'store/slices/user-slice/user-slice-role-functions';

import {
    generateUploadFileList,
    getHandleBeforeUpload,
    getSignsUploadHandler,
    useButtonsUploadImitation,
} from '../uploader-list.utils';
import {SIGN_UPLOAD_EXTENSIONS} from './constants/upload-list-signs-constants';
import {FileListUploader} from './file-list-uploader';
import {ISignFilesErrors} from './sign-and-file-list-uploader.types';
import {SignListUploader} from './sign-list-uploader';

import './sign-and-file-list-uploader.less';

interface ISignAndFileListUploaderProps {
    disabled?: boolean;
    buttonText?: string;
    buttonSignText?: string;
    buttonDownloadDocumentText?: string;
    url?: string;
    signUrl?: string;
    fileList?: IFileDto[];
    onChange?: (fileList: UploadFile<Blob>[]) => void;
    dataKeyForDownload?: string;
    requestCommentId?: string;
    isDownloadDocumentList?: boolean;
}

/**
 * Поле для загрузки файлов с подписями
 * @param fileList конечный массив обьектов в которых главными полями будут file: сам файл, sign: его подпись
 * @param buttonText
 * @param buttonSignText
 * @param buttonDownloadAllDocuments
 * @param disabled
 * @param onChange
 * @param url
 * @param dataKeyForDownload
 * @param signUrl
 * @param requestCommentId
 * @constructor
 */

export const SignAndFileListUploader: FC<ISignAndFileListUploaderProps> = ({
    fileList = [],
    buttonText = 'Выбрать файл',
    buttonSignText = 'Массовое подписание',
    buttonDownloadDocumentText = 'Скачать все',
    isDownloadDocumentList,
    disabled,
    onChange,
    url,
    dataKeyForDownload,
    requestCommentId,
    signUrl,
}: ISignAndFileListUploaderProps) => {
    const dispatch = useAppDispatch();
    const [signsErrors, setSignsErrors] = useState<ISignFilesErrors>({
        isErrorNamesConfirmVisible: false,
        isErrorEmptyFilesConfirmVisible: false,
        errorSignFilesNames: [],
        errorEmptySignFilesNames: [],
    });
    const {handleClick: handleFileClick} = useButtonsUploadImitation();
    const {handleClick: handleSignClick} = useButtonsUploadImitation();

    const documentsSigningMethod = useAppSelector(selectDocumentsSigningMethod);

    const {hasUserRoleFunction, isLoadingUserFunctions} = useUserRoleFunctions({
        sections: [REQUESTS_ENTITY_NAME],
    });

    useEffect(() => {
        dispatch(loadDocumentsSigningMethod());
    }, []);

    const normalizedFileList = generateUploadFileList(
        fileList as IFileDto[],
        {url, dataKeyForDownload, signUrl},
    ) ?? [];

    const handleFileBeforeUpload = getHandleBeforeUpload(normalizedFileList, onChange);
    const handleSignsUpload = getSignsUploadHandler(normalizedFileList, setSignsErrors, onChange, true);

    const downloadDocumentList = () => {
        dispatch(
            createActionGetFile({
                referenceUrl: `${url}/all-files-by-comment/${requestCommentId}`,
                requestType: ERequestType.GET,
            }),
        );
    };

    const confirmErrorNameMessage = (
        <ConfirmModal
            title="Отсутствует подходящий файл для следующих файлов подписи"
            visible={signsErrors?.isErrorNamesConfirmVisible || false}
            content={signsErrors?.errorSignFilesNames}
            onCancel={() => setSignsErrors(p => ({
                ...p,
                isErrorNamesConfirmVisible: false,
                errorSignFilesNames: [],
            }))}
            cancelText="Отмена"
            onConfirm={() => setSignsErrors(p => ({
                ...p,
                isErrorNamesConfirmVisible: false,
                errorSignFilesNames: [],
            }))}
            useWrapperDivElementAsContainer
            modalClassName="confirm-error-message"
        />
    );

    const confirmErrorEmptyFilesMessage = (
        <ConfirmModal
            title="Следующие файлы ЭП пустые"
            visible={signsErrors?.isErrorEmptyFilesConfirmVisible || false}
            content={signsErrors?.errorEmptySignFilesNames}
            onCancel={() => setSignsErrors(p => ({
                ...p,
                isErrorEmptyFilesConfirmVisible: false,
                errorEmptySignFilesNames: [],
            }))}
            cancelText="Отмена"
            onConfirm={() => setSignsErrors(p => ({
                ...p,
                isErrorEmptyFilesConfirmVisible: false,
                errorEmptySignFilesNames: [],
            }))}
            useWrapperDivElementAsContainer
        />
    );

    const buttonDownloadDocumentList = (
        <Space
            direction="vertical"
            className="button-download-document-list"
        >
            <Button
                type="primary"
                onClick={downloadDocumentList}
            >{buttonDownloadDocumentText}
            </Button>
        </Space>
    );

    return (
        <>
            {isDownloadDocumentList && buttonDownloadDocumentList}
            {signsErrors?.isErrorNamesConfirmVisible && confirmErrorNameMessage}
            {signsErrors?.isErrorEmptyFilesConfirmVisible && confirmErrorEmptyFilesMessage}
            <div
                className="sing-and-file-list-uploader"
            >
                <div className="files">
                    <Upload
                        disabled={disabled}
                        showUploadList={false}
                        fileList={normalizedFileList}
                        beforeUpload={handleFileBeforeUpload}
                        multiple
                    >
                        <Space direction="vertical">
                            {disabled && <Typography.Text style={{lineHeight: '36px'}}>Файлы</Typography.Text>}
                            {!disabled && (
                                <SimpleActionButton
                                    icon="File"
                                    type="primary"
                                    title={buttonText}
                                    onClick={handleFileClick}
                                />
                            )}
                        </Space>
                    </Upload>
                    <FileListUploader
                        fileList={normalizedFileList}
                        onChange={onChange}
                        disabled={disabled}
                    />
                </div>

                <div className="signs">
                    {documentsSigningMethod === EDocumentsSigningMethod.CRYPTO_PRO_PLUGIN
                        && hasUserRoleFunction(EUserRoleFunction.SIGN_FILE_DOC) && !disabled
                        ? (
                            <SignWithCryptoProInput
                                fileList={normalizedFileList
                                    .filter(nFile => !nFile.attachmentId && !nFile.signName && !!nFile.file)
                                    .map(nFile => nFile.file as RcFile)}
                                documentsToSignInDB={normalizedFileList
                                    .filter(nFile => (!isNil(nFile.attachmentId) || !isNil(nFile.id))
                                    && !nFile.signName).map(nFile => ({
                                        attachId: nFile.attachmentId ? nFile.attachmentId as string : `${nFile?.id}`,
                                        documentName: nFile.name,
                                    }))}
                                onChange={signedFiles => {
                                    handleSignsUpload(signedFiles, signedFiles);
                                }}
                                isSignaturesListHidden
                                buttonLabel={buttonSignText}
                                entityName={REQUESTS_ENTITY_NAME}
                            />
                        ) : (
                            <Upload
                                accept={SIGN_UPLOAD_EXTENSIONS}
                                disabled={disabled}
                                showUploadList={false}
                                multiple
                                beforeUpload={handleSignsUpload}
                            >

                                <Space direction="vertical">
                                    {!disabled && documentsSigningMethod === EDocumentsSigningMethod.UPLOAD_EDS_FILE ? (
                                        <Spin
                                            spinning={isLoadingUserFunctions}
                                            size="small"
                                            style={{width: '100%'}}
                                        >
                                            <SimpleActionButton
                                                type="primary"
                                                title={buttonSignText}
                                                onClick={handleSignClick}
                                            />
                                        </Spin>
                                    ) : (
                                        <Typography.Text style={{lineHeight: '36px'}}>
                                            Файлы подписи
                                        </Typography.Text>
                                    )}
                                </Space>
                            </Upload>
                        )}
                    <SignListUploader
                        fileList={normalizedFileList}
                        setSignsErrors={setSignsErrors}
                        onChange={onChange}
                        disabled={disabled}
                    />
                </div>
            </div>
        </>
    );
};
